import { FC, KeyboardEvent } from "react";
import { LucideIcon, icons } from "lucide-react"; // Import icons and LucideIcon type

export interface IconRadioOption {
  label: string;
  value: string;
  icon: keyof typeof icons; // Use keyof typeof icons for type-safe icon names
}

interface IconRadioGroupProps {
  name: string;
  options: IconRadioOption[];
  selectedValue: string;
  onChange: (value: string) => void;
  label?: string;
}

const IconRadioGroup: FC<IconRadioGroupProps> = ({
  name,
  options,
  selectedValue,
  onChange,
  label,
}) => {
  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    value: string,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onChange(value);
    }

    // Arrow key navigation
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      const currentIndex = options.findIndex(
        (option) => option.value === selectedValue,
      );
      const nextIndex = (currentIndex + 1) % options.length;
      onChange(options[nextIndex].value);

      // Focus the next element
      setTimeout(() => {
        const nextElement = document.querySelector(
          `[data-option-value="${options[nextIndex].value}"]`,
        ) as HTMLElement;
        nextElement?.focus();
      }, 0);
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      const currentIndex = options.findIndex(
        (option) => option.value === selectedValue,
      );
      const prevIndex =
        currentIndex === 0 ? options.length - 1 : currentIndex - 1;
      onChange(options[prevIndex].value);

      // Focus the previous element
      setTimeout(() => {
        const prevElement = document.querySelector(
          `[data-option-value="${options[prevIndex].value}"]`,
        ) as HTMLElement;
        prevElement?.focus();
      }, 0);
    }
  };

  return (
    <div className="my-4">
      {label && (
        <label className="mb-2 block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div
        className="flex flex-wrap gap-4 md:gap-4"
        role="radiogroup"
        aria-labelledby={label ? `${name}-label` : undefined}
      >
        {options.map((option) => {
          const IconComponent: LucideIcon = icons[option.icon];
          const isSelected = selectedValue === option.value;

          return (
            <div
              key={option.value}
              data-option-value={option.value}
              className={`focus-visible:ring-soluud-primary flex w-full cursor-pointer items-center justify-start rounded-lg border p-4 transition-all duration-200 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none md:h-24 md:min-h-24 md:w-24 md:min-w-24 md:flex-col md:justify-center ${
                isSelected
                  ? "border-soluud-primary bg-soluud-primary-light text-soluud-primary"
                  : "hover:border-soluud-primary focus:border-soluud-primary border-gray-300 bg-white text-gray-800"
              } `}
              onClick={() => onChange(option.value)}
              onKeyDown={(e) => handleKeyDown(e, option.value)}
              tabIndex={isSelected ? 0 : -1} // Only selected item is tabbable
              role="radio"
              aria-checked={isSelected}
              aria-labelledby={`${name}-${option.value}-label`}
            >
              {IconComponent && (
                <IconComponent
                  size={24}
                  className="mr-3 flex-shrink-0 md:mr-0 md:mb-1"
                />
              )}
              <span
                id={`${name}-${option.value}-label`}
                className="text-sm leading-tight font-medium break-words md:text-center md:text-xs"
              >
                {option.label}
              </span>
              {/* Hidden radio input for accessibility and form submission */}
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={() => onChange(option.value)}
                className="sr-only"
                tabIndex={-1} // Remove from tab order since we handle focus on the container
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default IconRadioGroup;
